import { Box, Stack, Typography } from "@mui/material";
import { MprcContainer } from "components";
import { UserItemDtoPaginatedList, UserPanelStatisticDto, UserProfileDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListUsersDrawer } from "views/foundation/user/drawers/ListUsersDrawer";

interface IProps {
    user: UserProfileDto;
    viewData: UserPanelStatisticDto | null;
}

/**
 * Painel lateral de estatística do perfil de usuário
 * @param props
 */
export const PanelUserStatistic: React.FC<IProps> = (props) => {
    const vData = props.viewData!;
    const user = props.user!;

    const { t } = useTranslation("knowledge");
    const api = useCoreApi();
    const [dialog, setDialog] = useState<any>();

    const hasValues = vData.totalPublications > 0
        || vData.totalAnswers > 0
        || vData.totalViews > 0
        || vData.totalLikes > 0
        || vData.followers > 0
        || vData.following > 0
        || vData.historyPoints > 0
        || vData.monthPoints > 0
        || vData.totalBadges > 0;

    ///
    const handleFollowers = () => {
        setDialog(
            <ListUsersDrawer
                title={t("Feed.Panels.UserStatistic.FollowersTitle")}
                subtitle={user.name}
                onClose={() => setDialog(null)}
                onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                    new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                        api.v1.knowledgeFeed.getFeedPanelUserFollowers({ userId: user.userId, searchText, pageNumber, pageSize }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };

    ///
    const handleFollowing = () => {
        setDialog(
            <ListUsersDrawer
                title={t("Feed.Panels.UserStatistic.FollowingText")}
                subtitle={user.name}
                onClose={() => setDialog(null)}
                onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                    new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                        api.v1.knowledgeFeed.getFeedPanelUserFollowing({ userId: user.userId, searchText, pageNumber, pageSize }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return hasValues
        ? (
            <>
                {dialog}

                <MprcContainer margin="dense">
                    <Typography variant="h3">{t("Feed.Panels.UserStatistic.Title")}</Typography>
                </MprcContainer>

                <Stack direction="column" spacing={1}>
                    {
                        (vData.totalPublications > 0 || vData.totalAnswers > 0) &&
                        <Box>
                            <table style={{ borderSpacing: 0 }}>
                                <thead>
                                    <tr>
                                        <td colSpan={2}>
                                            <Typography variant="subtitle1"><b>{t("Feed.Panels.UserStatistic.ContributionSubtitle")}</b></Typography>
                                        </td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        vData.totalPublications > 0 &&
                                        <tr>
                                            <td align="right"><span>{Formaters.localeNumber(vData.totalPublications)}</span></td>
                                            <td><span>{t("Feed.Panels.UserStatistic.PublicationText", { count: vData.totalPublications })}</span></td>
                                        </tr>
                                    }
                                    {
                                        vData.totalAnswers > 0 &&
                                        <tr>
                                            <td align="right"><span>{Formaters.localeNumber(vData.totalAnswers)}</span></td>
                                            <td><span>{t("Feed.Panels.UserStatistic.AnswersText", { count: vData.totalAnswers })}</span></td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </Box>
                    }

                    {
                        (vData.totalViews > 0 || vData.totalLikes > 0) &&
                        <Box>
                            <table>
                                <thead>
                                    <tr>
                                        <td colSpan={2}>
                                            <Typography variant="subtitle1"><b>{t("Feed.Panels.UserStatistic.ReputationSubtitle")}</b></Typography>
                                        </td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        vData.totalViews > 0 &&
                                        <tr>
                                            <td align="right"><span>{Formaters.localeNumber(vData.totalViews)}</span></td>
                                            <td><span>{t("Feed.Panels.UserStatistic.ViewsText", { count: vData.totalViews })}</span></td>
                                        </tr>
                                    }
                                    {
                                        vData.totalLikes > 0 &&
                                        <tr>
                                            <td align="right"><span>{Formaters.localeNumber(vData.totalLikes)}</span></td>
                                            <td><span>{t("Feed.Panels.UserStatistic.LikesText", { count: vData.totalLikes })}</span></td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </Box>
                    }

                    {
                        (vData.followers > 0 || vData.following > 0) &&
                        <Box>
                            <table>
                                <thead>
                                    <tr>
                                        <td colSpan={2}>
                                            <Typography variant="subtitle1"><b>{t("Feed.Panels.UserStatistic.ConnectionsSubtitle")}</b></Typography>
                                        </td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        vData.followers > 0 &&
                                        <tr>
                                            <td>
                                                <Typography
                                                    variant="linkAction"
                                                    component="a"
                                                    onClick={handleFollowers}
                                                >
                                                    <span>{Formaters.localeNumber(vData.followers)}</span>
                                                    &nbsp;
                                                    <span>{t("Feed.Panels.UserStatistic.FollowerText", { count: vData.followers })}</span>
                                                </Typography>
                                            </td>
                                        </tr>
                                    }
                                    {
                                        vData.following > 0 &&
                                        <tr>
                                            <td>
                                                <Typography
                                                    variant="linkAction"
                                                    component="a"
                                                    onClick={handleFollowing}
                                                >
                                                    <span>{Formaters.localeNumber(vData.following)}</span>
                                                    &nbsp;
                                                    <span>{t("Feed.Panels.UserStatistic.FollowingText", { count: vData.following })}</span>
                                                </Typography>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </Box>
                    }

                    {
                        (vData.historyPoints > 0 || vData.monthPoints > 0 || vData.totalBadges > 0) &&
                        <Box>
                            <table>
                                <thead>
                                    <tr>
                                        <td colSpan={2}>
                                            <Typography variant="subtitle1"><b>{t("Feed.Panels.UserStatistic.AwardSubtitle")}</b></Typography>
                                        </td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        vData.historyPoints > 0 &&
                                        <tr>
                                            <td align="right"><span>{Formaters.localeNumber(vData.historyPoints)}</span></td>
                                            <td><span>{t("Feed.Panels.UserStatistic.HistoryPointsText", { count: vData.historyPoints })}</span></td>
                                        </tr>
                                    }
                                    {
                                        vData.monthPoints > 0 &&
                                        <tr>
                                            <td align="right"><span>{Formaters.localeNumber(vData.monthPoints)}</span></td>
                                            <td><span>{t("Feed.Panels.UserStatistic.MonthPoints", { count: vData.monthPoints })}</span></td>
                                        </tr>
                                    }
                                    {
                                        vData.totalBadges > 0 &&
                                        <tr>
                                            <td align="right"><span>{Formaters.localeNumber(vData.totalBadges)}</span></td>
                                            <td><span>{t("Feed.Panels.UserStatistic.BadgesText", { count: vData.totalBadges })}</span></td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </Box>
                    }
                </Stack>
            </>
        )
        : null;
};
